import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { CreditCard, Hash, Image as ImageIcon, Phone, Building2 } from "lucide-react";
import { toast } from "sonner";

import { get, post } from "@/lib/api-client";
import { newIdempotencyKey } from "@/lib/idempotency";
import { formatCurrency } from "@/lib/format";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";

export function PackagePaymentPage() {
  const { purchaseId } = useParams();
  const nav = useNavigate();
  const [selectedGateway, setSelectedGateway] = useState<any>(null);

  // 1. Fetch the specific purchase details
  const purchases = useQuery({
    queryKey: ["membership-purchases"],
    queryFn: () => get<any[]>("/membership/purchases/list"),
  });
  
  const purchase = purchases.data?.find(p => p.id === Number(purchaseId));

  // 2. Fetch active Payment Gateways
  const gateways = useQuery({
    queryKey: ["public-payment-gateways"],
    queryFn: () => get<any[]>("/public/payment-gateways"),
  });

  const { register, handleSubmit, watch, formState: { errors } } = useForm();
  const methodValue = watch("method");

  // 🛠️ FIXED: Aggressively update the selected gateway so the account number ALWAYS shows
  useEffect(() => {
    if (methodValue && gateways.data) {
      const found = gateways.data.find(g => String(g.name) === String(methodValue));
      setSelectedGateway(found || null);
    } else {
      setSelectedGateway(null);
    }
  }, [methodValue, gateways.data]);

  const submit = useMutation({
    mutationFn: (data: any) => {
      const formData = new FormData();
      formData.append("membership_purchase_id", purchaseId!);
      formData.append("method", data.method);
      formData.append("sender_account", data.sender_account);
      formData.append("transaction_reference", data.transaction_reference);
      formData.append("amount", purchase!.plan_price); // Force exact amount
      formData.append("payment_date", new Date().toISOString());
      if (data.notes) formData.append("notes", data.notes);
      if (data.screenshot && data.screenshot.length > 0) {
        formData.append("screenshot", data.screenshot[0]);
      }
      
      return post("/payments/payments/membership", formData, {
        headers: { "Content-Type": "multipart/form-data" },
        idempotencyKey: newIdempotencyKey()
      });
    },
    onSuccess: () => {
      toast.success("Payment submitted successfully. Please wait for admin approval.");
      nav("/dashboard/membership");
    },
  });

  if (purchases.isLoading || gateways.isLoading) return <Skeleton className="h-96" />;
  if (!purchase) return <div>Purchase not found.</div>;

  // Formatting options for select dropdown
  const gatewayOptions = (gateways.data || []).map(g => ({ value: g.name, label: g.name }));

  return (
    <div className="max-w-2xl mx-auto space-y-6 animate-fade-in">
      <button onClick={() => nav(-1)} className="text-sm font-semibold text-surface-500 hover:text-surface-900">
        ← Back
      </button>

      <Card>
        <CardHeader>
          <CardTitle>Pay for {purchase.plan_name}</CardTitle>
          <div className="text-xl font-bold text-brand-700">{formatCurrency(purchase.plan_price)}</div>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit((d) => submit.mutate(d))} className="space-y-5">
            
            <Select
              label="Select Payment Method"
              options={gatewayOptions}
              placeholder="-- Select a payment method --"
              error={errors.method?.message as string}
              {...register("method", { required: "Method is required" })}
            />

            {/* 🛠️ FIXED: Beautifully highlight the Admin's Receiver Account Number */}
            {selectedGateway && (
              <div className="p-5 bg-brand-50 border border-brand-200 rounded-xl flex flex-col gap-3 animate-slide-up">
                <div className="flex items-center gap-2 text-brand-900 font-bold">
                  <Building2 className="w-5 h-5 text-brand-600" />
                  Payment Instructions
                </div>
                
                <div className="bg-white p-3 rounded-lg border border-brand-100 shadow-sm text-center">
                  <div className="text-xs text-surface-500 uppercase tracking-widest font-bold mb-1">
                    Send Money To this Account
                  </div>
                  <div className="text-2xl font-mono font-black text-brand-700 select-all">
                    {selectedGateway.account_details}
                  </div>
                </div>

                {selectedGateway.instructions && (
                  <div className="text-sm text-brand-800 whitespace-pre-wrap leading-relaxed px-1">
                    {selectedGateway.instructions}
                  </div>
                )}
              </div>
            )}

            <div className="grid sm:grid-cols-2 gap-4">
              <Input
                label="Your Account Number (Sender)"
                placeholder="e.g. 017XXXXXXX"
                leftIcon={<Phone className="h-4 w-4" />}
                error={errors.sender_account?.message as string}
                {...register("sender_account", { required: "Sender account is required" })}
              />

              <Input
                label="Transaction ID / Reference"
                placeholder="e.g. TRX123456789"
                leftIcon={<Hash className="h-4 w-4" />}
                error={errors.transaction_reference?.message as string}
                {...register("transaction_reference", { required: "Transaction ID is required" })}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-surface-800 mb-1.5">Payment Screenshot (Optional)</label>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-surface-100 flex items-center justify-center border border-surface-200 shrink-0">
                  <ImageIcon className="h-5 w-5 text-surface-400" />
                </div>
                <input 
                  type="file" 
                  accept="image/*" 
                  className="w-full text-sm text-surface-600 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-xs file:font-semibold file:bg-brand-50 file:text-brand-700 hover:file:bg-brand-100 transition-colors cursor-pointer" 
                  {...register("screenshot")} 
                />
              </div>
            </div>

            <Button type="submit" size="lg" fullWidth loading={submit.isPending} leftIcon={<CreditCard className="h-4 w-4" />}>
              Confirm & Submit Payment
            </Button>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}